import { camelCase } from 'lodash';
import { useEffect } from 'react';
import {
  createHttpLink,
  DefaultContext as Context,
  ApolloLink,
  ApolloClient,
  InMemoryCache,
  ApolloProvider,
  Observable,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { relayStylePagination } from '@apollo/client/utilities';
import * as Sentry from '@sentry/react';

import { useAuth } from './auth/auth-context';
import { FullPageLoader } from './components/loader';
import { getRenderAuthToken } from './lib/render-auth';
import { UserInitiatedAbort, AbortError, isAbortError } from './lib/error/utils/abort';
import { TimeoutError } from './lib/error/utils/timeout';
import { generateUUID } from './lib/utils';
import { setupChatWidget } from './lib/utils/support';

const UNAUTHENTICATED_REQUESTS = ['SignUp', 'SsoProviderIdForEmail'];

export const AuthorisedApolloProvider = ({ children }: { children: React.ReactNode }) => {
  const { user, getToken, logOut, loading } = useAuth();

  useEffect(() => {
    if (user?.email !== null && user?.email !== undefined) {
      setupChatWidget({ email: user.email });
    }
  }, [user?.email]);

  if (loading) {
    return <FullPageLoader />;
  }

  const httpLink = createHttpLink({
    uri: '/graphql',
    headers: {
      'X-Supersimple-Client': `Supersimple-app ${APP_VERSION}`,
    },
    fetch: (uri, options) => {
      return fetch(uri, options).catch((err) => {
        if (err.name === 'AbortError') {
          throw new TimeoutError(`Request timed out`);
        } else if (err === UserInitiatedAbort) {
          throw new AbortError();
        }

        throw err;
      });
    },
  });

  const timeoutLink = setContext((_, previousContext) => {
    const fetchOptions: Context = {};

    if (previousContext.fetchOptions?.signal !== undefined) {
      fetchOptions.signal = previousContext.fetchOptions.signal;
    }

    return { fetchOptions };
  });

  // Log any GraphQL errors or network error that occurred
  const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
    const { traceId } = operation.getContext() as { traceId?: string };
    const operationName = operation.operationName;

    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, locations, path }) => {
        if (message.includes('Bad token')) {
          logOut();
        }
        Sentry.addBreadcrumb({
          category: 'graphql.error',
          level: 'error',
          message,
          data: { operationName, traceId, path, locations },
        });
        console.error(
          `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
        );
      });
    }

    if (networkError) {
      Sentry.addBreadcrumb({
        category: 'graphql.network',
        level: 'error',
        message: networkError.message,
        data: { operationName, traceId },
      });
      console.error(`[Network error]: ${networkError}`);

      if (isAbortError(networkError)) {
        throw new AbortError();
      }
    }
  });

  const authLink = setContext(async ({ operationName }) => {
    if (operationName !== undefined && UNAUTHENTICATED_REQUESTS.includes(operationName)) {
      return {};
    }

    // Headless render capture authenticates with a service-user access token
    // instead of a Firebase session.
    const renderAuthToken = getRenderAuthToken();
    if (renderAuthToken !== null) {
      return {
        headers: {
          Authorization: `Token ${renderAuthToken}`,
        },
      };
    }

    const token = await getToken();

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  });

  const RetryDelayMs = 2000;

  const retryLink = new ApolloLink((operation, forward) => {
    return new Observable((observer) => {
      let cancelled = false;
      let retryTimer: ReturnType<typeof setTimeout> | null = null;
      let currentSub: { unsubscribe: () => void } | null = null;

      const attempt = (isRetry: boolean) => {
        if (cancelled) {
          return;
        }
        const isMutation = operation.query.definitions.some(
          (definition) =>
            definition.kind === 'OperationDefinition' && definition.operation === 'mutation',
        );
        currentSub = forward(operation).subscribe({
          next: (result) => observer.next(result),
          error: (err) => {
            if (!isRetry && !isMutation && [502, 503, 504].includes(err?.statusCode)) {
              retryTimer = setTimeout(() => attempt(true), RetryDelayMs);
            } else {
              observer.error(err);
            }
          },
          complete: () => observer.complete(),
        });
      };

      attempt(false);

      return () => {
        cancelled = true;
        if (retryTimer !== null) {
          clearTimeout(retryTimer);
        }
        currentSub?.unsubscribe();
      };
    });
  });

  const traceLink = new ApolloLink((operation, forward) => {
    const traceId = generateUUID();
    operation.setContext(({ headers = {} }) => ({
      headers: {
        ...headers,
        'X-Trace-ID': traceId,
      },
      traceId,
    }));
    return forward(operation);
  });

  const client = new ApolloClient({
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'no-cache',
      },
    },
    cache: new InMemoryCache({
      typePolicies: {
        Account: {
          keyFields: ['accountId'],
          fields: {
            query: relayStylePagination([
              'baseModelId',
              'pipeline',
              'sort',
              'first',
              'variables',
              'timezone',
            ]),
            conversation: {
              read(_, { args, toReference }) {
                return toReference({
                  __typename: 'Conversation',
                  conversationId: args?.conversationId,
                });
              },
            },
          },
        },
        Model: { keyFields: ['modelId'], fields: { entities: relayStylePagination() } },
        EntityRelation: {
          fields: {
            entities: relayStylePagination(),
          },
        },
        Conversation: {
          keyFields: ['conversationId'],
          fields: {
            messages: {
              merge(existing, incoming, { mergeObjects }) {
                return incoming.map((message: { id: string }) =>
                  mergeObjects(
                    (existing ?? []).find((m: { id: string }) => m.id === message.id) ?? {},
                    message,
                  ),
                );
              },
            },
          },
        },
      },
      dataIdFromObject: (obj) => {
        const fieldName = `${camelCase(obj.__typename)}Id`;
        if (typeof obj[fieldName] === 'string') {
          return `${obj.__typename}:${obj[fieldName]}`;
        }
        return undefined;
      },
    }),
    link: ApolloLink.from([authLink, timeoutLink, errorLink, retryLink, traceLink, httpLink]),
  });

  return <ApolloProvider client={client}> {children} </ApolloProvider>;
};
